import { ChangeEvent, FC, Fragment, useEffect, useRef, useState } from 'react';
// Models
import { IMyUser } from 'app/models/User';
import IInsuranceCase from 'app/models/Case';
import { IDocument } from 'app/store/DMSDocuments/DMSDocuments.models';
import ILabel from 'app/models/Label';
import { RootState } from 'app/store';
// Types
import Colors from 'app/types/Colors';
import Labels from 'app/components/Labels';
import { AuditOperations } from 'app/store/Audit/Audit.types';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { createAuditRecord } from 'app/store/Audit/Audit.async';
import { patchDocument } from 'app/store/DMSDocuments/DMSDocuments.async';
// Selectors
import { selectMyUser } from 'app/store/Users/Users.selectors';
import { selectInsuranceCase } from 'app/store/Cases/Cases.selectors';
import { selectDocumentEntity, selectLoading } from 'app/store/DMSDocuments/DMSDocuments.selectors';
import { selectPushAlert } from 'app/store/AppUINotifications/AppUINotifications.selectors';
// Mui
import { Box, Chip, Typography } from '@mui/material';
// Components
import Dialog from 'app/components/Dialog';
import ColorPalette from 'app/components/ColorPalette';
import DocumentPushAlert from 'app/components/DocumentPushAlert';
import { Input, Button } from 'app/components/Mui';
// i18next
import { useTranslation } from 'react-i18next';
// Services
import LocalStorageService from 'app/services/LocalStorage.service';

type Props = {
  open: boolean;
  onClose: () => void;
  documentId: number;
  labels?: ILabel[];
};

const DocumentLabelsDialog:FC<Props> = ({
  // Props
  open, onClose, documentId, labels = []
}) => {
  const { t } = useTranslation();

  const auditLabelsRef = useRef<{
    added: ILabel[];
    removed: ILabel[];
  }>({ added: [], removed: [] });

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const myUser:IMyUser | null = useAppSelector(selectMyUser);
  const insuranceCase:IInsuranceCase | null = useAppSelector(selectInsuranceCase);
  const loading:boolean = useAppSelector(selectLoading);
  const document = useAppSelector((state:RootState) => selectDocumentEntity(state, { documentId })) as IDocument;
  const alert = useAppSelector((state:RootState) => selectPushAlert(state, {
    category: 'document',
    type: 'labels'
  }));

  const [ name, setName ] = useState('');
  const [ color, setColor ] = useState<Colors|string>(Colors.Red);
  const [ stateLabels, setStateLabels ] = useState<ILabel[]>(labels);
  const [ addedLabels, setAddedLabels ] = useState<ILabel[]>([]);
  const [ removedLabels, setRemovedLabels ] = useState<ILabel[]>([]);
  const [ recentLabels, setRecentLabels ] = useState<ILabel[]>([]);
  
  const prevStateLabelsRef = useRef<ILabel[]>([]);

  useEffect(() => {
    prevStateLabelsRef.current = stateLabels;
  }, [stateLabels]);
    
  const applyLabelsChanges = () => {
    if (!document) return;
  
    const documentLabels = document.labels || [];
    const prevLabels = prevStateLabelsRef.current;
  
    const removedByOther = stateLabels.filter(
      local => !documentLabels.some(db => db.name === local.name && db.color === local.color)
    );
  
    const addedByOther = documentLabels.filter(
      db => !stateLabels.some(local => local.name === db.name && local.color === db.color)
    );
  
    const baseLabels = documentLabels.filter(
      l => !removedLabels.some(r => r.name === l.name && r.color === l.color)
    );
  
    const baseWithStatuses = baseLabels.map(l => ({
      ...l,
      status: addedByOther.some(a => a.name === l.name && a.color === l.color)
        ? 'addedByOther'
        : undefined
    }));
  
    const combined = [...baseWithStatuses, ...addedLabels.map(l => ({ ...l }))];
  
    const disappearing = removedByOther.map(l => ({ ...l, status: 'removedByOther' }));
  
    const nextLabels: (ILabel & { status?: string })[] = [];

    for (const prev of prevLabels) {
      const stillExists = combined.find(l => l.name === prev.name && l.color === prev.color);
      const removed = disappearing.find(l => l.name === prev.name && l.color === prev.color);
      if (stillExists) {
        nextLabels.push(stillExists);
      } else if (removed) {
        nextLabels.push(removed);
      }
    }
  
    combined.forEach((l) => {
      if (!nextLabels.find(x => x.name === l.name && x.color === l.color)) {
        nextLabels.push(l);
      }
    });

    auditLabelsRef.current = {
      added: [...addedLabels],
      removed: [...removedLabels],
    };
  
    setStateLabels(nextLabels);
    setAddedLabels([]);
    setRemovedLabels([]);
  
    setTimeout(() => {
      setStateLabels(current =>
        current.filter((l:any) => l.status !== 'removedByOther')
      );
    }, 0);
  };

  const handleSave = async () => {
    try {
      const nextLabels = stateLabels.map(label => ({ name: label.name, color: label.color }));
      setStateLabels(nextLabels);
      await dispatch(patchDocument({ documentId, data: { labels: nextLabels } })).unwrap();
      LocalStorageService.setDocumentsLabels(addedLabels);
      evokeAuditCreation();
      onClose();
    } catch(error){}
  }

  const evokeAuditCreation = () => {
    const { added, removed } = auditLabelsRef.current;
    const allAddedLabels = [...addedLabels, ...added];
    const allRemovedLabels = [...removedLabels, ...removed];

    if ( !allAddedLabels.length && !allRemovedLabels.length ) return;

    dispatch(createAuditRecord({
      operation: allAddedLabels.length
        ? AuditOperations.DocumentLabelAdding
        : AuditOperations.DocumentLabelRemoving
      ,
      data: {
        caseId: insuranceCase?.id || 0,
        userName: myUser?.name || '',
        addedLabels: allAddedLabels.map((label:ILabel) => label.name),
        removedLabels: allRemovedLabels.map((label:ILabel) => label.name),
        fileName: document.name
      }
    }));
  }

  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }

  const handleAddLabel = () => {
    if ( !name || !color ) return;

    const label = { name, color };

    const foundedIndex = removedLabels.findIndex((l:ILabel) => l.name === label.name && l.color === label.color);

    if ( foundedIndex !== -1 ){
      setRemovedLabels((prevState:ILabel[]) => prevState.filter((_:any, idx) => idx !== foundedIndex));
    } else {
      setAddedLabels([...addedLabels, label]);
    }

    setStateLabels((prevState:ILabel[]) => [...prevState, label])
    setName('');
    setColor(Colors.Red);
  }

  const handleRemoveLabel = (index:number) => {
    const label = stateLabels[index];

    const foundedIndex = addedLabels.findIndex((l:ILabel) => l.name === label.name && l.color === label.color);

    if ( foundedIndex !== -1 ){
      setAddedLabels((prevState:ILabel[]) => prevState.filter((_:any, idx) => idx !== foundedIndex));
    } else {
      setRemovedLabels([...removedLabels, label]);
    }

    setStateLabels((prevState:ILabel[]) => prevState.filter((_:any, idx) => idx !== index));
  }

  const handleColorClick = (nextColor:Colors|string) => setColor(nextColor);

  const handleClick = (label:ILabel) => {
    const foundedIndex = removedLabels.findIndex((l:ILabel) => l.name === label.name && l.color === label.color);

    if ( foundedIndex !== -1 ){
      setRemovedLabels((prevState:ILabel[]) => prevState.filter((_:any, idx) => idx !== foundedIndex));
    } else {
      setAddedLabels([...addedLabels, label]);
    }
    setStateLabels(prevState => [...prevState, label]);
  };

  useEffect(() => {
    setRecentLabels(LocalStorageService.getDocumentsLabels());
  }, []);

  const isDisabled = !!alert && alert.documentId === documentId;

  const actions = (
    <Fragment>
      <Button
        name={`Cancel document label dialog`}
        onClick={onClose}
      >{t('buttons.close')}</Button>
      <Button
        name={`Save document label dialog`}
        onClick={handleSave}
        variant="contained"
        color="primary"
        disabled={isDisabled}
        loading={loading}
      >{t('buttons.save')}</Button>
    </Fragment>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('components.filesSidebar.documentLabelsTitle')}
      actions={actions}
    >
      <DocumentPushAlert documentId={documentId} type="labels" applyFunc={applyLabelsChanges} />
      {recentLabels.length ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 4 }}>
          <Typography sx={{ fontSize: '14px', color: 'rgba(0,0,0,0.6)', fontWeight: 500 }}>
            {t('components.filesSidebar.recentlyAddedLabel')}
          </Typography>
          {recentLabels.map((label, index) => (
            <Chip
              key={`label-item-${index}-${label.name}-${label.color}`} label={label.name}
              size="small" className={label.color}
              sx={{ borderRadius: '4px', cursor: 'pointer' }}
              onClick={() => handleClick(label)}
            />
          ))}
        </Box>
      ) : null}
      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <Box flexGrow={1}>
          <Input
            label={t('labels.name')} value={name}
            onChange={handleChange}
            margin="none"
          />
        </Box>
        <Box sx={{ pt: '10px' }}>
          <Button
            name="Add label"
            disabled={!name}
            onClick={handleAddLabel}
            variant="contained"
            color="primary"
          >{t('buttons.add')}</Button>
        </Box>
      </Box>
      <ColorPalette sx={{ mb: 4 }} activeColors={[color]} showNoColorValue={false}>
        {(color) => (
          <ColorPalette.Item
            key={color}
            color={color}
            onClick={handleColorClick}
          />
        )}
      </ColorPalette>
      <Labels
        id={documentId}
        labels={stateLabels}
        onRemove={handleRemoveLabel}
      />
    </Dialog>
  )
}

export default DocumentLabelsDialog;